import { ChangeDetectorRef, Component, computed, inject, OnInit, signal } from '@angular/core';
import { Location, NgTemplateOutlet } from '@angular/common';
import { Path, RoutePath } from 'src/app/shared/route';
import { Router } from '@angular/router';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import { featherBell, featherChevronLeft, featherPlus } from '@ng-icons/feather-icons';
import { SearchComponent } from './search/search.component';
import { NotificationComponent } from './notification/notification.component';
import { HistoryTriggerComponent } from 'src/app/components/history-sheet/history-trigger.component';
import { PromoPopupTriggerComponent } from 'src/app/components/promotion/promo-popup-trigger.component';
import { AuthService } from 'src/app/services/auth/auth.service';
import { ENVIRONMENT } from 'src/app/providers/environment';
import { LayoutService } from 'src/app/services/layout/layout.service';
import { BaseComponent } from 'src/app/components/_base/base.component';
import deepDiff from 'deep-diff';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslatePipe, TranslateService, _ } from '@ngx-translate/core';
import { MatMenuModule } from '@angular/material/menu';
import { captureException } from '@sentry/angular';
import { CTAButton } from 'src/app/types/general';
import { MatDialog } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { AgentChatStore } from 'src/app/services/agent-chat/agent-chat-store.service';
import { helpCentreUrl } from 'src/app/shared/help-centre';

@Component({
  templateUrl: './header.component.html',
  standalone: true,
  selector: 'app-header',
  imports: [
    NgIconComponent,
    MatButtonModule,
    SearchComponent,
    NotificationComponent,
    HistoryTriggerComponent,
    PromoPopupTriggerComponent,
    NgTemplateOutlet,
    TranslatePipe,
    MatMenuModule,
    MatIconModule,
  ],
  viewProviders: [
    provideIcons({
      featherBell,
      featherPlus,
      featherChevronLeft,
    }),
  ],
  styleUrl: './header.component.scss',
})
export class HeaderComponent extends BaseComponent implements OnInit {
  private readonly location = inject(Location);
  private readonly authService = inject(AuthService);
  private readonly dialog = inject(MatDialog);
  private readonly env = inject(ENVIRONMENT);
  private readonly layoutService = inject(LayoutService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly router = inject(Router);
  private readonly snackbar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);
  private readonly agentChatStore = inject(AgentChatStore);

  readonly addClientPath = RoutePath.getPath(Path['admin/clients/add']).fullPath;

  readonly isAdmin = computed(() => this.authService.isAdmin);
  readonly helpCentreUrl = helpCentreUrl(this.env.frontDomain);

  ctaButton: CTAButton | undefined;

  readonly onboardingReactivating = signal(false);

  ngOnInit(): void {
    this.sub$ = this.layoutService.ctaButton$.subscribe((ctaButton) => {
      this.ctaButton = ctaButton;

      this.cdr.detectChanges();
    });
  }

  onBack() {
    this.location.back();
  }

  /** A merchant reaches a person through the support chat (GOO-457). */
  onContactSupport() {
    this.agentChatStore.setOpen(true);
  }

  async onNew() {
    const Component = await import('./compose-notification/compose-notification.component').then(
      (c) => c.ComposeNotificationComponent,
    );

    this.dialog.open(Component);
  }

  get privacyPolicyUrl() {
    return `${this.env.frontDomain}/privacy-policy`;
  }

  onOnboarding() {
    this.onboardingReactivating.set(true);

    this.authService
      .updateProfile({ diffs: deepDiff({ finishedOnboarding: true }, { finishedOnboarding: false }) ?? [] })
      .subscribe({
        next: () => {
          this.router.navigate(RoutePath.getPath(Path['dashboard']).fullPath);
          this.onboardingReactivating.set(false);
        },
        error: (error) => {
          captureException(error);
          this.onboardingReactivating.set(false);
          this.snackbar.open(
            this.translate.instant(_('error.error-occured')),
            this.translate.instant(_('common.close')),
            { panelClass: 'snackbar-error' },
          );
        },
      });
  }
}
